import React, { useState } from 'react';
import { 
  Plus, 
  Wand2, 
  Printer, 
  Clock, 
  Trash2, 
  Loader2,
  Calendar
} from 'lucide-react';
import type { Practice } from '../../types';

interface MobileUtilityNavProps {
  selectedPractice: Practice | null;
  isMobileView: boolean;
  isGenerating: boolean;
  onGeneratePlan: () => void;
  onAddDrill: () => void;
  onClearDrills: () => void;
  onToggleTimeFormat: () => void;
  onPrint: () => void;
  onAddPractice: () => void;
}

export function MobileUtilityNav({
  selectedPractice,
  isMobileView,
  isGenerating,
  onGeneratePlan,
  onAddDrill,
  onClearDrills,
  onToggleTimeFormat,
  onPrint,
  onAddPractice
}: MobileUtilityNavProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Only show when we're in mobile view AND have a selected practice
  if (!isMobileView || !selectedPractice) {
    return null;
  }

  const handleGeneratePlanClick = () => {
    onGeneratePlan();
    setIsOpen(false);
  };

  const handleClearDrillsClick = () => {
    onClearDrills();
    setIsOpen(false);
  };

  return (
    <div
      className={`fixed bottom-24 z-40 flex flex-col-reverse items-end gap-3 transition-all ${
        isOpen ? 'right-20' : 'right-6'
      }`}
    >
      {/* Flyout Menu */}
      {isOpen && (
        <div className="flex flex-col gap-2 mb-2 animate-fadeIn">
          <button
            key="clear-drills"
            onClick={handleClearDrillsClick}
            className="h-12 w-full rounded-full bg-white shadow-lg flex items-center gap-3 px-4 text-red-600 hover:bg-red-100"
            title="Clear Drills"
          >
            <Trash2 size={24} />
            <span className="text-sm font-medium">Clear Drills</span>
          </button>
          <button
            key="toggle-time-format"
            onClick={() => {
              onToggleTimeFormat();
              setIsOpen(false);
            }}
            className="h-12 w-full rounded-full bg-white shadow-lg flex items-center gap-3 px-4 text-stone hover:bg-primary hover:bg-opacity-10"
            title="Toggle Time Format"
          >
            <Clock size={24} />
            <span className="text-sm font-medium">Time Format</span>
          </button>
          <button
            key="print"
            onClick={() => {
              onPrint();
              setIsOpen(false);
            }}
            className="h-12 w-full rounded-full bg-white shadow-lg flex items-center gap-3 px-4 text-stone hover:bg-gray-100"
            title="Print"
          >
            <Printer size={24} />
            <span className="text-sm font-medium">Print</span>
          </button>
          <button
            key="create-practice"
            onClick={() => {
              onAddPractice();
              setIsOpen(false);
            }}
            className="h-12 w-full rounded-full bg-white shadow-lg flex items-center gap-3 px-4 text-stone hover:bg-gray-100"
            title="Create Practice"
          >
            <Calendar size={24} />
            <span className="text-sm font-medium">Create Practice</span>
          </button>
          <button
            key="add-drill"
            onClick={() => {
              onAddDrill();
              setIsOpen(false);
            }}
            className="h-12 w-full rounded-full bg-white shadow-lg flex items-center gap-3 px-4 text-stone hover:bg-gray-100"
            title="Add Drill"
          >
            <Plus size={24} />
            <span className="text-sm font-medium">Add Drill</span>
          </button>
          <button
            key="generate-plan"
            onClick={handleGeneratePlanClick}
            className="h-12 w-full rounded-full bg-primary shadow-lg flex items-center gap-3 px-4 text-white hover:bg-primary-hover"
            disabled={isGenerating}
            title="Generate Plan"
          >
            {isGenerating ? (
              <>
                <Loader2 size={24} className="animate-spin" />
                <span className="text-sm font-medium">Generating...</span>
              </>
            ) : (
              <>
                <Wand2 size={24} />
                <span className="text-sm font-medium">Generate Plan</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Main FAB Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="h-14 w-14 rounded-full bg-primary shadow-xl flex items-center justify-center text-white transition-transform hover:bg-primary-hover animate-pulse-once"
        style={{
          transform: isOpen ? 'rotate(45deg)' : 'none',
          transition: 'transform 0.2s ease-in-out'
        }}
      >
        <Plus size={28} />
      </button>
    </div>
  );
}

export default MobileUtilityNav; 